import React, {
  useState,
  useEffect,
  useRef,
  useImperativeHandle,
  forwardRef,
  useCallback,
  type CSSProperties,
} from 'react';
import { createPortal } from 'react-dom';
import { Box, Text, Menu, ActionIcon, Group, Divider } from '@mantine/core';
import type { PortalProps } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { IconEye, IconEyeOff, IconPlayerPlay, IconPlayerPause, IconChevronLeft, IconChevronRight, IconDots } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { Annotation } from '@/types/Annotation.model';
import { ANNOTATION_DEFAULTS } from '@/utils/annotationConstants';
import {
  ANNOTATION_CAMERA_TRANSITION_SETTLE_MS,
  applyAnnotationNavigationCamera,
  resolveAnnotationNavigationCamera,
} from '@/utils/modelViewerZoom';
import { hexToRgba } from '@/utils/colorUtils';
import * as modelViewer from '@google/model-viewer';
import { useBrandForeground } from '@/utils/brandForeground';
import {
  VIEWER_CONTROL_CHROME_HOVER_BG,
  VIEWER_CONTROL_CHROME_STYLE,
} from '@/components/ModelViewer/viewerControlChrome';

interface AnnotationNavigatorProps {
  annotations: Annotation[];
  modelViewerRef: React.RefObject<modelViewer.ModelViewerElement>;
  color: string;
  onAnnotationChange?: (annotation: Annotation | null) => void;
  isAnnotationMode?: boolean;
  onAnnotationsVisibleChange?: (visible: boolean) => void;
  hasAnimationSelector?: boolean;
  inline?: boolean; // If true, button will not use absolute positioning (for flex layouts)
  /** When set, the navigation bar (prev/next + title) is rendered into this DOM element id, so it can be placed e.g. center while the menu button stays inline */
  barPortalTargetId?: string;
  /** Distance from bottom for the 3-dot button and (when not portaled) the bar. E.g. '40px' to move them up. */
  bottomSpacing?: string;
  /** Distance from right for the 3-dot button when not inline. E.g. '16px' to pin to bottom-right. */
  menuButtonRight?: string;
  /** When the viewer is in theater or fullscreen, pass a base (e.g. 2100) so portaled menus sit above backdrop/stage overlays. */
  immersiveLayerBaseZ?: number;
  /** When the stage is in native browser fullscreen, portal menus into that element (body stays behind). */
  menuPortalProps?: Omit<PortalProps, 'children'>;
}

export interface AnnotationNavigatorRef {
  navigateToAnnotation: (annotation: Annotation) => void;
  navigateNext: () => void;
  navigatePrevious: () => void;
}

const ANNOTATION_BAR_MOBILE_BREAKPOINT = '(max-width: 768px)';
/** Space reserved for play / animation / menu buttons on the viewer’s bottom-right (mobile). */
const MOBILE_ANNOTATION_BAR_RIGHT_RESERVE = '200px';

/** Portal target for the annotation title bar — centered on desktop, compact left pill on mobile. */
export function useAnnotationBarPortalTargetStyle(
  overrides?: CSSProperties
): CSSProperties {
  const isMobileBar = useMediaQuery(ANNOTATION_BAR_MOBILE_BREAKPOINT);
  return {
    position: 'absolute',
    bottom: '16px',
    left: isMobileBar ? '16px' : '50%',
    transform: isMobileBar ? undefined : 'translateX(-50%)',
    zIndex: 12,
    display: 'flex',
    alignItems: 'center',
    justifyContent: isMobileBar ? 'flex-start' : 'center',
    width: isMobileBar ? 'max-content' : undefined,
    maxWidth: isMobileBar ? `calc(100% - ${MOBILE_ANNOTATION_BAR_RIGHT_RESERVE})` : undefined,
    pointerEvents: isMobileBar ? 'none' : undefined,
    ...overrides,
  };
}

export const AnnotationNavigator = forwardRef<AnnotationNavigatorRef, AnnotationNavigatorProps>(({
  annotations,
  modelViewerRef,
  color,
  onAnnotationChange,
  isAnnotationMode = false,
  onAnnotationsVisibleChange,
  hasAnimationSelector = true,
  inline = false,
  barPortalTargetId,
  bottomSpacing,
  menuButtonRight,
  immersiveLayerBaseZ,
  menuPortalProps,
}, ref) => {
  const { t } = useTranslation();
  const iconColor = useBrandForeground(color);
  /** Narrow annotation bar on small screens so it does not overlap the viewer’s right-side controls */
  const isMobileBar = useMediaQuery(ANNOTATION_BAR_MOBILE_BREAKPOINT);
  const [annotationsVisible, setAnnotationsVisible] = useState<boolean>(true);
  const [autopilotActive, setAutopilotActive] = useState<boolean>(false);
  const [currentAnnotationIndex, setCurrentAnnotationIndex] = useState<number>(-1);
  const [activeAnnotation, setActiveAnnotation] = useState<Annotation | null>(null);
  const autopilotIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Helper function to apply camera settings for an annotation
  const applyCameraToAnnotation = useCallback((annotation: Annotation) => {
    const mv = modelViewerRef.current;
    if (!mv) return;
    
    // Enable smooth camera transitions
    (mv as any).interactionPrompt = 'none';
    
    // Temporarily disable camera-controls to allow programmatic orbit changes
    const hadCameraControls = mv.hasAttribute('camera-controls');
    if (hadCameraControls) {
      mv.removeAttribute('camera-controls');
    }
    
    const cameraState = resolveAnnotationNavigationCamera(mv, annotation, {
      orbit: ANNOTATION_DEFAULTS.MODEL_VIEWER_ORBIT,
      fieldOfView: ANNOTATION_DEFAULTS.FIELD_OF_VIEW,
    });

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        applyAnnotationNavigationCamera(mv, cameraState);

        if (hadCameraControls) {
          setTimeout(() => {
            mv.setAttribute('camera-controls', '');
          }, ANNOTATION_CAMERA_TRANSITION_SETTLE_MS);
        }
      });
    });
  }, []);

  // Navigate to a specific annotation with smooth animation; trigger GLB animation if it's an animation point
  const navigateToAnnotation = useCallback((annotation: Annotation, index: number) => {
    applyCameraToAnnotation(annotation);

    // If this is an animation point, play the selected animation track (same as when clicking the hotspot)
    if (annotation.type === 'animation' && annotation.animationTrack) {
      const mv = modelViewerRef.current;
      if (mv) {
        try {
          (mv as any).animationName = annotation.animationTrack;
          mv.play();
        } catch (error) {
          console.error('Error playing animation:', error);
        }
      }
    }

    setActiveAnnotation(annotation);
    setCurrentAnnotationIndex(index);
    if (onAnnotationChange) {
      onAnnotationChange(annotation);
    }
  }, [applyCameraToAnnotation, onAnnotationChange]);

  // Navigate to next annotation
  const navigateNext = useCallback(() => {
    if (annotations.length === 0) return;
    const nextIndex = currentAnnotationIndex < annotations.length - 1 
      ? currentAnnotationIndex + 1 
      : 0;
    navigateToAnnotation(annotations[nextIndex], nextIndex);
  }, [annotations, currentAnnotationIndex, navigateToAnnotation]);

  // Navigate to previous annotation
  const navigatePrevious = useCallback(() => {
    if (annotations.length === 0) return;
    const prevIndex = currentAnnotationIndex > 0 
      ? currentAnnotationIndex - 1 
      : annotations.length - 1;
    navigateToAnnotation(annotations[prevIndex], prevIndex);
  }, [annotations, currentAnnotationIndex, navigateToAnnotation]);

  // Expose navigation API via ref (after next/prev are defined)
  useImperativeHandle(ref, () => ({
    navigateToAnnotation: (annotation: Annotation) => {
      const index = annotations.findIndex((ann) => ann.id === annotation.id);
      if (index !== -1) {
        navigateToAnnotation(annotation, index);
      }
    },
    navigateNext,
    navigatePrevious,
  }), [annotations, navigateToAnnotation, navigateNext, navigatePrevious]);

  // Toggle annotations visibility
  const toggleAnnotationsVisibility = useCallback(() => {
    const newVisibility = !annotationsVisible;
    setAnnotationsVisible(newVisibility);
    if (onAnnotationsVisibleChange) {
      onAnnotationsVisibleChange(newVisibility);
    }
  }, [annotationsVisible, onAnnotationsVisibleChange]);

  // Toggle autopilot
  const toggleAutopilot = useCallback(() => {
    if (autopilotActive) {
      // Stop autopilot
      if (autopilotIntervalRef.current) {
        clearInterval(autopilotIntervalRef.current);
        autopilotIntervalRef.current = null;
      }
      setAutopilotActive(false);
    } else {
      // Start autopilot
      if (annotations.length === 0) return;
      // Start from first annotation if none is active
      if (currentAnnotationIndex === -1) {
        navigateToAnnotation(annotations[0], 0);
      }
      setAutopilotActive(true);
    }
  }, [autopilotActive, annotations, currentAnnotationIndex, navigateToAnnotation]);

  // Autopilot effect - automatically navigate through annotations
  useEffect(() => {
    if (autopilotActive && annotations.length > 0) {
      autopilotIntervalRef.current = setInterval(() => {
        setCurrentAnnotationIndex((prevIndex) => {
          const nextIndex = prevIndex < annotations.length - 1 ? prevIndex + 1 : 0;
          const nextAnnotation = annotations[nextIndex];
          if (nextAnnotation) {
            applyCameraToAnnotation(nextAnnotation);
            // Trigger animation when navigating to an animation point (same as click)
            if (nextAnnotation.type === 'animation' && nextAnnotation.animationTrack && modelViewerRef.current) {
              try {
                (modelViewerRef.current as any).animationName = nextAnnotation.animationTrack;
                modelViewerRef.current.play();
              } catch (error) {
                console.error('Error playing animation:', error);
              }
            }
            setActiveAnnotation(nextAnnotation);
            if (onAnnotationChange) {
              onAnnotationChange(nextAnnotation);
            }
          }
          return nextIndex;
        });
      }, 5000); // 5 seconds per annotation (slower for smoother transitions)

      return () => {
        if (autopilotIntervalRef.current) {
          clearInterval(autopilotIntervalRef.current);
          autopilotIntervalRef.current = null;
        }
      };
    } else {
      if (autopilotIntervalRef.current) {
        clearInterval(autopilotIntervalRef.current);
        autopilotIntervalRef.current = null;
      }
    }
  }, [autopilotActive, annotations, onAnnotationChange, applyCameraToAnnotation]);

  // Cleanup autopilot on unmount
  useEffect(() => {
    return () => {
      if (autopilotIntervalRef.current) {
        clearInterval(autopilotIntervalRef.current);
      }
    };
  }, []);

  // Expose annotationsVisible state to parent
  useEffect(() => {
    if (onAnnotationsVisibleChange) {
      onAnnotationsVisibleChange(annotationsVisible);
    }
  }, [annotationsVisible, onAnnotationsVisibleChange]);

  if (isAnnotationMode || annotations.length === 0) {
    return null;
  }

  const menuPortalZ = immersiveLayerBaseZ != null ? immersiveLayerBaseZ + 600 : 1001;
  const barZ = immersiveLayerBaseZ != null ? immersiveLayerBaseZ + 25 : 10;
  const dotsFloatingZ = immersiveLayerBaseZ != null ? immersiveLayerBaseZ + 45 : 12;

  return (
    <>
      {/* Annotation Navigation Menu - Bottom right, next to AR/Play buttons */}
      <Menu
        shadow="md"
        width={220}
        position="top-end"
        zIndex={menuPortalZ}
        withinPortal
        portalProps={menuPortalProps}
      >
        <Menu.Target>
          <button
            style={{
              ...VIEWER_CONTROL_CHROME_STYLE,
              borderRadius: '50%',
              width: '40px',
              height: '40px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.2s ease',
              cursor: 'pointer',
              padding: 0,
              color: iconColor,
              fontSize: '0.875rem',
              fontWeight: 500,
              transform: 'scale(1)',
              ...(inline ? {} : {
                position: 'absolute',
                bottom: bottomSpacing ?? '16px',
                right: menuButtonRight ?? (hasAnimationSelector ? '164px' : '116px'),
                zIndex: dotsFloatingZ,
              }),
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'scale(1.05)';
              e.currentTarget.style.backgroundColor = VIEWER_CONTROL_CHROME_HOVER_BG;
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'scale(1)';
              e.currentTarget.style.backgroundColor = VIEWER_CONTROL_CHROME_STYLE.backgroundColor;
              e.currentTarget.style.boxShadow = VIEWER_CONTROL_CHROME_STYLE.boxShadow;
            }}
            onMouseDown={(e) => {
              e.currentTarget.style.transform = 'scale(0.95)';
            }}
            onMouseUp={(e) => {
              e.currentTarget.style.transform = 'scale(1.05)';
            }}
            className="desktop-ar-buttons"
          >
            <IconDots size={20} />
          </button>
        </Menu.Target>

        <Menu.Dropdown>
          <Menu.Item
            leftSection={annotationsVisible ? <IconEyeOff size={16} /> : <IconEye size={16} />}
            onClick={toggleAnnotationsVisibility}
          >
            {annotationsVisible ? t('editProduct.hideAnnotations') : t('editProduct.showAnnotations')}
          </Menu.Item>
          
          <Menu.Item
            leftSection={autopilotActive ? <IconPlayerPause size={16} /> : <IconPlayerPlay size={16} />}
            onClick={toggleAutopilot}
          >
            {autopilotActive ? t('editProduct.stopAutopilot') : t('editProduct.startAutopilot')}
          </Menu.Item>

          <Divider />

          <Menu.Label>{t('editProduct.annotations')}</Menu.Label>
          {annotations.map((annotation, index) => (
            <Menu.Item
              key={annotation.id}
              onClick={() => navigateToAnnotation(annotation, index)}
              style={{
                backgroundColor: currentAnnotationIndex === index ? `${color}15` : undefined,
                color: currentAnnotationIndex === index ? color : undefined,
                fontWeight: currentAnnotationIndex === index ? 600 : 400,
              }}
            >
              {`${index + 1}. ${annotation.title || annotation.label}`}
            </Menu.Item>
          ))}
        </Menu.Dropdown>
      </Menu>

      {/* Annotation Navigation Bar - inline here, or portaled to center when barPortalTargetId is set */}
      {activeAnnotation && (() => {
        const barContent = (
          <Box
            style={{
              position: barPortalTargetId ? 'relative' : 'absolute',
              bottom: barPortalTargetId ? undefined : (bottomSpacing ?? '20px'),
              left: barPortalTargetId ? undefined : isMobileBar ? '16px' : '50%',
              transform: barPortalTargetId ? undefined : isMobileBar ? undefined : 'translateX(-50%)',
              zIndex: barZ,
              display: 'inline-flex',
              alignItems: 'center',
              gap: isMobileBar ? 6 : 12,
              padding: isMobileBar ? '4px 8px' : '6px 12px',
              width: isMobileBar ? 'max-content' : undefined,
              maxWidth: isMobileBar
                ? barPortalTargetId
                  ? '100%'
                  : `calc(100% - ${MOBILE_ANNOTATION_BAR_RIGHT_RESERVE})`
                : undefined,
              minWidth: 0,
              overflow: isMobileBar ? 'hidden' : undefined,
              boxSizing: 'border-box',
              background: 'rgba(0, 0, 0, 0.75)',
              backdropFilter: 'blur(10px)',
              color: '#fff',
              borderRadius: '20px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
              fontSize: isMobileBar ? '11px' : '12px',
              pointerEvents: 'auto',
            }}
          >
            {annotations.length > 1 && (
              <ActionIcon
                variant="subtle"
                color="white"
                onClick={navigatePrevious}
                size="sm"
                style={{ color: '#fff', flexShrink: 0 }}
              >
                <IconChevronLeft size={isMobileBar ? 14 : 16} />
              </ActionIcon>
            )}
            <Text
              fw={500}
              size="xs"
              style={{
                flex: isMobileBar ? '1 1 auto' : undefined,
                minWidth: isMobileBar ? 0 : 150,
                textAlign: isMobileBar ? 'left' : 'center',
                fontSize: isMobileBar ? '11px' : '12px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {activeAnnotation.title || `${t('editProduct.annotation')} ${currentAnnotationIndex + 1}`}
            </Text>
            {annotations.length > 1 && (
              <ActionIcon
                variant="subtle"
                color="white"
                onClick={navigateNext}
                size="sm"
                style={{ color: '#fff', flexShrink: 0 }}
              >
                <IconChevronRight size={isMobileBar ? 14 : 16} />
              </ActionIcon>
            )}
          </Box>
        );
        if (barPortalTargetId && typeof document !== 'undefined') {
          const target = document.getElementById(barPortalTargetId);
          if (target) return createPortal(barContent, target);
        }
        return barContent;
      })()}
    </>
  );
});

